import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'lib/Auth';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { deleteBulkUserProfile, deleteUserProfile } from 'requests/userProfile';

interface UseUserProfilesConfig<T> {
    // API fetch function
    fetchFunction: (params: any[], auth: any) => Promise<any>;

    // Dynamic parameters for the fetch function
    buildParams?: (state: {
        currentPage: number;
        rowsPerPage: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        searchTerm: string;
    }) => any[];

    // Optional reload trigger
    reload?: boolean;
    setReload?: (reload: boolean) => void;

    // Custom success messages
    successMessages?: {
        delete?: string;
        bulkDelete?: string;
    };

    // Response transformer
    transformResponse?: (response: any) => { data: T[]; totalCount: number };
}

interface TableData<T> {
    data: T[];
    totalCount: number;
}

interface UseUserProfilesReturn<T> {
    // Data state
    users: T[];
    tableData: TableData<T>;
    loading: boolean;
    error: string | null;
    hasInitialLoad: boolean;
    tableKey: string;
    searchTerm: string;

    // Pagination state
    currentPage: number;
    rowsPerPage: number;

    // Sorting state
    sortBy: string;
    sortOrder: 'asc' | 'desc';

    // Selection state
    selectedRows: any[];

    // Pagination actions
    handlePageChange: (newPage: number) => void;
    handleRowsPerPageChange: (newRowsPerPage: number) => void;

    // Sorting actions
    handleSortChange: (field: string, direction: 'asc' | 'desc') => void;

    // Selection actions
    setSelectedRows: (rows: any[]) => void;
    setSearchTerm: (term: string) => void;

    // CRUD actions
    handleRemove: (profileId: number) => void;
    handleDeleteMultiple: () => void;

    // Utility actions
    refreshData: () => void;
    resetState: () => void;
}

/**
 * Generic hook for managing user profiles with pagination, sorting, search, and CRUD operations
 * Shared logic for both admin users and business unit users
 *
 * @template T - The type of user profile data
 */
export const useUserProfiles = <T = any>({
    fetchFunction,
    buildParams,
    reload,
    setReload,
    successMessages = {
        delete: 'Profile deleted successfully',
        bulkDelete: 'Profile(s) deleted successfully'
    },
    transformResponse
}: UseUserProfilesConfig<T>): UseUserProfilesReturn<T> => {

    const { token } = useAuth();

    // Store function props in refs to prevent infinite loops
    const fetchFunctionRef = useRef(fetchFunction);
    const buildParamsRef = useRef(buildParams);
    const transformResponseRef = useRef(transformResponse);

    // Update refs when props change
    useEffect(() => {
        fetchFunctionRef.current = fetchFunction;
        buildParamsRef.current = buildParams;
        transformResponseRef.current = transformResponse;
    }, [fetchFunction, buildParams, transformResponse]);

    // Data state
    const [users, setUsers] = useState<T[]>([]);
    const [tableData, setTableData] = useState<TableData<T>>({ data: [], totalCount: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // UI state
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sorting state
    const [sortBy, setSortBy] = useState('createdOn');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Table key for forcing re-render when needed
    const [tableKey, setTableKey] = useState(`userProfiles_${Date.now()}`);

    // Reset all state to initial values
    const resetState = useCallback(() => {
        setUsers([]);
        setTableData({ data: [], totalCount: 0 });
        setLoading(false);
        setError(null);
        setHasInitialLoad(false);
        setSelectedRows([]);
        setCurrentPage(0);
        setRowsPerPage(10);
        setSortBy('createdOn');
        setSortOrder('desc');
        setTableKey(`userProfiles_${Date.now()}`);
        setSearchTerm('');
    }, []);

    // Fetch users
    const fetchUsers = useCallback(async (showLoading = true) => {
        if (!token) {
            setTableData({ data: [], totalCount: 0 });
            setUsers([]);
            setHasInitialLoad(true);
            return;
        }

        try {
            if (showLoading) {
                setLoading(true);
            }
            setError(null);

            // Build parameters dynamically using ref
            const params = buildParamsRef.current ?
                buildParamsRef.current({ currentPage, rowsPerPage, sortBy, sortOrder, searchTerm }) :
                [currentPage + 1, rowsPerPage, sortBy, sortOrder, searchTerm];

            const response = await fetchFunctionRef.current(params, { token });

            // Transform response using ref
            let userData: T[] = [];
            let totalCount = 0;

            if (transformResponseRef.current) {
                const transformed = transformResponseRef.current(response);
                userData = transformed.data;
                totalCount = transformed.totalCount;
            } else if (response?.data && Array.isArray(response.data)) {
                userData = response.data;
                totalCount = response.meta?.totalRecords || response.data.length;
            }

            // Update state
            const transformedData = {
                data: userData,
                totalCount: totalCount
            };

            setUsers(userData);
            setTableData(transformedData);
            setHasInitialLoad(true);
            setTableKey(`userProfiles_${Date.now()}`);

        } catch (err: any) {
            console.error('Error fetching user profiles:', err);
            const errorMessage = err.message || 'Failed to fetch users';
            setError(errorMessage);
            setTableData({ data: [], totalCount: 0 });
            setUsers([]);
            renderToast(toast.TYPE.ERROR, errorMessage);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [token, currentPage, rowsPerPage, sortBy, sortOrder, searchTerm]);

    // Initial load and dependency-based reload
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Handle external reload trigger
    useEffect(() => {
        if (reload) {
            fetchUsers();
            setReload?.(false);
        }
    }, [reload, fetchUsers, setReload]);

    // Pagination handlers
    const handlePageChange = useCallback((newPage: number) => {
        const pageIndex = typeof newPage === 'number' ? newPage - 1 : newPage;
        setCurrentPage(pageIndex >= 0 ? pageIndex : 0);
        setSelectedRows([]);
    }, []);

    const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(0);
        setSelectedRows([]);
    }, []);

    // Sorting handler
    const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
        setSortBy(field);
        setSortOrder(direction);
        setCurrentPage(0);
        setSelectedRows([]);
    }, []);

    // Search handler
    const handleSearchTermChange = useCallback((term: string) => {
        setSearchTerm(term);
        setCurrentPage(0);
        setSelectedRows([]);
    }, []);

    // Delete single user
    const handleRemove = useCallback(async (profileId: number) => {
        try {
            await deleteUserProfile([profileId], { token });
            setReload?.(true);
            renderToast(toast.TYPE.SUCCESS, successMessages.delete!);
        } catch (err: any) {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    }, [token, setReload, successMessages]);

    // Delete multiple users
    const handleDeleteMultiple = useCallback(async () => {
        if (selectedRows.length === 0) {
            return;
        }
        const profileIds = selectedRows.map(row => row.id);
        try {
            await deleteBulkUserProfile([profileIds], { token });
            setReload?.(true);
            renderToast(toast.TYPE.SUCCESS, successMessages.bulkDelete!);
        } catch (err: any) {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    }, [selectedRows, token, setReload, successMessages]);

    // Utility actions
    const refreshData = useCallback(() => {
        fetchUsers(true);
    }, [fetchUsers]);

    // Enhanced selection handler
    const handleSelectionChange = useCallback((rows: any[]) => {
        setSelectedRows(rows);
    }, []);

    return {
        // Data state
        users,
        tableData,
        loading,
        error,
        hasInitialLoad,
        tableKey,
        searchTerm,

        // Pagination state
        currentPage,
        rowsPerPage,

        // Sorting state
        sortBy,
        sortOrder,

        // Selection state
        selectedRows,

        // Pagination actions
        handlePageChange,
        handleRowsPerPageChange,

        // Sorting actions
        handleSortChange,

        // Selection actions
        setSelectedRows: handleSelectionChange,
        setSearchTerm: handleSearchTermChange,

        // CRUD actions
        handleRemove,
        handleDeleteMultiple,

        // Utility actions
        refreshData,
        resetState
    };
};
